import React, { useEffect, useState, useRef } from 'react';
import { UploadIcon, EyeIcon, EyeOffIcon, CheckCircle2Icon, ChevronDownIcon, ChevronRightIcon, BuildingIcon, CheckIcon } from 'lucide-react';
import { Agency, PropertyData, LoanData, Party } from '../types';
import { mockPropertyData, mockLoanData, mockParties, mockAgencies } from '../data/mockData';
interface OrderFormPageProps {
  onSubmit: (agency: Agency) => void;
}
export function OrderFormPage({
  onSubmit
}: OrderFormPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSsn, setShowSsn] = useState<Record<string, boolean>>({});
  // Accordion State
  const [expandedSection, setExpandedSection] = useState<string | null>('agency');
  // Agency Selection State
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [isAgencyDropdownOpen, setIsAgencyDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Form State (pre-filled from mock data)
  const [property, setProperty] = useState<PropertyData>(mockPropertyData);
  const [loan, setLoan] = useState<LoanData>(mockLoanData);
  const [parties, setParties] = useState<Party[]>(mockParties);
  const [notes, setNotes] = useState('');
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsAgencyDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSubmit = () => {
    if (!selectedAgency) {
      alert('Please select a settlement agency first.');
      setExpandedSection('agency');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      setTimeout(() => {
        onSubmit(selectedAgency);
      }, 2000);
    }, 1500);
  };
  const toggleSsnVisibility = (id: string) => {
    setShowSsn(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };
  if (showSuccess) {
    return <div className="h-full flex flex-col items-center justify-center bg-slate-50" data-id="element-206">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center max-w-md w-full" data-id="element-207">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 mb-5" data-id="element-208">
            <CheckCircle2Icon className="h-7 w-7 text-emerald-600" data-id="element-209" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-210">
            Order Placed Successfully!
          </h2>
          <p className="text-sm text-slate-600 mb-6" data-id="element-211">
            Your title order has been sent to {selectedAgency?.name}.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200" data-id="element-212">
            <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-medium" data-id="element-213">
              Order Number
            </p>
            <p className="text-lg font-mono font-bold text-slate-900" data-id="element-214">
              TO-2024-8891
            </p>
          </div>
          <div className="animate-pulse flex items-center justify-center text-xs text-blue-600 font-medium" data-id="element-215">
            Redirecting to Dashboard...
          </div>
        </div>
      </div>;
  }
  // Section Definitions for Accordion
  const sections = [{
    id: 'agency',
    title: 'Settlement Agency',
    isComplete: !!selectedAgency,
    summary: selectedAgency ? `${selectedAgency.name} — ${selectedAgency.city}, ${selectedAgency.state}` : 'No agency selected',
    content: <div className="relative" ref={dropdownRef} data-id="element-216">
          <label className="block text-xs font-medium text-slate-700 mb-1.5" data-id="element-217">
            Select Agency <span className="text-red-500" data-id="element-218">*</span>
          </label>

          <button type="button" onClick={() => setIsAgencyDropdownOpen(!isAgencyDropdownOpen)} className={`relative w-full cursor-default rounded-md bg-white py-3 pl-4 pr-10 text-left text-slate-900 shadow-sm ring-1 ring-inset focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6 ${!selectedAgency ? 'ring-rose-300 bg-rose-50/30' : 'ring-slate-300'}`} data-id="element-219">
            {selectedAgency ? <div className="flex items-center justify-between" data-id="element-220">
                <div data-id="element-221">
                  <span className="block font-semibold text-slate-900" data-id="element-222">
                    {selectedAgency.name}
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5" data-id="element-223">
                    {selectedAgency.city}, {selectedAgency.state} •{' '}
                    {selectedAgency.phone}
                  </span>
                </div>
                <div className="flex gap-1.5 mr-2" data-id="element-224">
                  {selectedAgency.allowsPreOpen && <span className="inline-flex items-center rounded bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20" data-id="element-225">
                      Pre-Open
                    </span>}
                  {selectedAgency.titleSearchOnly && <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10" data-id="element-226">
                      Search Only
                    </span>}
                </div>
              </div> : <span className="block truncate text-slate-500" data-id="element-227">
                Select a settlement agency...
              </span>}
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4" data-id="element-228">
              <ChevronDownIcon className="h-5 w-5 text-slate-400" aria-hidden="true" data-id="element-229" />
            </span>
          </button>

          {isAgencyDropdownOpen && <ul className="absolute z-20 mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm" data-id="element-230">
              {mockAgencies.map(agency => <li key={agency.id} className={`relative cursor-pointer select-none py-3 pl-4 pr-9 hover:bg-slate-50 ${selectedAgency?.id === agency.id ? 'bg-blue-50/50' : ''}`} onClick={() => {
          setSelectedAgency(agency);
          setIsAgencyDropdownOpen(false);
          // Auto-advance to next section if this was the first time selecting
          if (!selectedAgency) {
            setTimeout(() => setExpandedSection('property'), 300);
          }
        }} data-id="element-231">
                  <div className="flex items-center justify-between" data-id="element-232">
                    <div data-id="element-233">
                      <span className={`block truncate ${selectedAgency?.id === agency.id ? 'font-semibold text-blue-900' : 'font-medium text-slate-900'}`} data-id="element-234">
                        {agency.name}
                      </span>
                      <span className="block truncate text-xs text-slate-500 mt-0.5" data-id="element-235">
                        {agency.city}, {agency.state} • {agency.phone}
                      </span>
                    </div>
                    <div className="flex gap-1.5" data-id="element-236">
                      {agency.allowsPreOpen && <span className="inline-flex items-center rounded bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20" data-id="element-237">
                          Pre-Open
                        </span>}
                      {agency.titleSearchOnly && <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10" data-id="element-238">
                          Search Only
                        </span>}
                    </div>
                  </div>

                  {selectedAgency?.id === agency.id && <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600" data-id="element-239">
                      <CheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-240" />
                    </span>}
                </li>)}
            </ul>}
        </div>
  }, {
    id: 'property',
    title: 'Property Information',
    isComplete: !!(property.address1 && property.city && property.state && property.zip),
    summary: property.address1 ? `${property.address1}, ${property.city} ${property.state} ${property.zip}` : 'No address entered',
    content: <div className="grid grid-cols-1 gap-y-5 gap-x-4 sm:grid-cols-6" data-id="element-241">
          <div className="sm:col-span-6" data-id="element-242">
            <label className="block text-xs font-medium text-slate-600" data-id="element-243">
              Street Address
            </label>
            <input type="text" value={property.address1} onChange={e => setProperty({
          ...property,
          address1: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-244" />
          </div>
          <div className="sm:col-span-6" data-id="element-245">
            <label className="block text-xs font-medium text-slate-600" data-id="element-246">
              Address Line 2 (Optional)
            </label>
            <input type="text" value={property.address2 || ''} onChange={e => setProperty({
          ...property,
          address2: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-247" />
          </div>
          <div className="sm:col-span-2 sm:col-start-1" data-id="element-248">
            <label className="block text-xs font-medium text-slate-600" data-id="element-249">
              City
            </label>
            <input type="text" value={property.city} onChange={e => setProperty({
          ...property,
          city: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-250" />
          </div>
          <div className="sm:col-span-1" data-id="element-251">
            <label className="block text-xs font-medium text-slate-600" data-id="element-252">
              State
            </label>
            <input type="text" value={property.state} onChange={e => setProperty({
          ...property,
          state: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-253" />
          </div>
          <div className="sm:col-span-1" data-id="element-254">
            <label className="block text-xs font-medium text-slate-600" data-id="element-255">
              ZIP
            </label>
            <input type="text" value={property.zip} onChange={e => setProperty({
          ...property,
          zip: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-256" />
          </div>
          <div className="sm:col-span-2" data-id="element-257">
            <label className="block text-xs font-medium text-slate-600" data-id="element-258">
              County
            </label>
            <input type="text" value={property.county} onChange={e => setProperty({
          ...property,
          county: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-259" />
          </div>
        </div>
  }, {
    id: 'loan',
    title: 'Loan Information',
    isComplete: !!(loan.amount && loan.number && loan.type),
    summary: `$${loan.amount.toLocaleString()} ${loan.type} ${loan.purpose} — Loan #${loan.number}`,
    content: <div className="grid grid-cols-1 gap-y-5 gap-x-4 sm:grid-cols-6" data-id="element-260">
          <div className="sm:col-span-3" data-id="element-261">
            <label className="block text-xs font-medium text-slate-600" data-id="element-262">
              Loan Number
            </label>
            <input type="text" value={loan.number} onChange={e => setLoan({
          ...loan,
          number: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6 bg-slate-50" readOnly data-id="element-263" />
          </div>
          <div className="sm:col-span-3" data-id="element-264">
            <label className="block text-xs font-medium text-slate-600" data-id="element-265">
              Loan Purpose
            </label>
            <div className="mt-1.5 flex rounded-lg bg-slate-100 p-1" data-id="element-266">
              <button type="button" onClick={() => setLoan({
            ...loan,
            purpose: 'Purchase'
          })} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${loan.purpose === 'Purchase' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-267">
                Purchase
              </button>
              <button type="button" onClick={() => setLoan({
            ...loan,
            purpose: 'Refinance'
          })} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${loan.purpose === 'Refinance' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-268">
                Refinance
              </button>
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-269">
            <label className="block text-xs font-medium text-slate-600" data-id="element-270">
              Loan Amount
            </label>
            <div className="mt-1.5 relative rounded-lg shadow-sm" data-id="element-271">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-272">
                <span className="text-slate-500 sm:text-sm" data-id="element-273">$</span>
              </div>
              <input type="number" value={loan.amount} onChange={e => setLoan({
            ...loan,
            amount: Number(e.target.value)
          })} className="block w-full rounded-lg border-0 py-2 pl-7 pr-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-274" />
            </div>
          </div>
          {loan.purpose === 'Purchase' && <div className="sm:col-span-2" data-id="element-275">
              <label className="block text-xs font-medium text-slate-600" data-id="element-276">
                Purchase Price
              </label>
              <div className="mt-1.5 relative rounded-lg shadow-sm" data-id="element-277">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-278">
                  <span className="text-slate-500 sm:text-sm" data-id="element-279">$</span>
                </div>
                <input type="number" value={loan.purchasePrice} onChange={e => setLoan({
            ...loan,
            purchasePrice: Number(e.target.value)
          })} className="block w-full rounded-lg border-0 py-2 pl-7 pr-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-280" />
              </div>
            </div>}
          <div className="sm:col-span-2" data-id="element-281">
            <label className="block text-xs font-medium text-slate-600" data-id="element-282">
              Loan Type
            </label>
            <select value={loan.type} onChange={e => setLoan({
          ...loan,
          type: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-283">
              <option data-id="element-284">Conventional</option>
              <option data-id="element-285">FHA</option>
              <option data-id="element-286">VA</option>
              <option data-id="element-287">USDA</option>
            </select>
          </div>
        </div>
  }, {
    id: 'parties',
    title: 'Parties',
    isComplete: parties.length > 0,
    summary: `${parties.filter(p => p.role === 'Borrower').length} Borrowers${loan.purpose === 'Purchase' ? `, ${parties.filter(p => p.role === 'Seller').length} Sellers` : ''}`,
    content: <div className="space-y-8" data-id="element-288">
          <div data-id="element-289">
            <div className="flex items-center justify-between mb-4" data-id="element-290">
              <h3 className="text-sm font-semibold text-slate-800" data-id="element-291">
                Borrowers
              </h3>
              <button className="text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-md px-2.5 py-1 transition-colors hover:bg-blue-50" data-id="element-292">
                + Add
              </button>
            </div>
            <div className="space-y-4" data-id="element-293">
              {parties.filter(p => p.role === 'Borrower').map(party => <div key={party.id} className="bg-white p-4 rounded-lg border border-slate-200 border-l-2 border-l-blue-500 shadow-sm" data-id="element-294">
                    <div className="grid grid-cols-1 gap-y-5 gap-x-4 sm:grid-cols-6" data-id="element-295">
                      <div className="sm:col-span-3" data-id="element-296">
                        <label className="block text-xs font-medium text-slate-600" data-id="element-297">
                          Full Name
                        </label>
                        <input type="text" value={party.name} readOnly className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 sm:leading-6 bg-white" data-id="element-298" />
                      </div>
                      <div className="sm:col-span-3" data-id="element-299">
                        <label className="block text-xs font-medium text-slate-600" data-id="element-300">
                          SSN
                        </label>
                        <div className="relative mt-1.5" data-id="element-301">
                          <input type={showSsn[party.id] ? 'text' : 'password'} value={party.ssn || ''} readOnly className="block w-full rounded-lg border-0 py-2 pl-3 pr-10 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 sm:leading-6 bg-white font-mono" data-id="element-302" />
                          <button type="button" onClick={() => toggleSsnVisibility(party.id)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600" data-id="element-303">
                            {showSsn[party.id] ? <EyeOffIcon className="h-4 w-4" data-id="element-304" /> : <EyeIcon className="h-4 w-4" data-id="element-305" />}
                          </button>
                        </div>
                      </div>
                      <div className="sm:col-span-3" data-id="element-306">
                        <label className="block text-xs font-medium text-slate-600" data-id="element-307">
                          Email
                        </label>
                        <input type="email" value={party.email} readOnly className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 sm:leading-6 bg-white" data-id="element-308" />
                      </div>
                      <div className="sm:col-span-3" data-id="element-309">
                        <label className="block text-xs font-medium text-slate-600" data-id="element-310">
                          Phone
                        </label>
                        <input type="text" value={party.phone} readOnly className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 sm:leading-6 bg-white" data-id="element-311" />
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>

          {loan.purpose === 'Purchase' && <>
              <div className="h-px bg-slate-200 w-full" data-id="element-312" />
              <div data-id="element-313">
                <div className="flex items-center justify-between mb-4" data-id="element-314">
                  <h3 className="text-sm font-semibold text-slate-800" data-id="element-315">
                    Sellers
                  </h3>
                  <button className="text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-md px-2.5 py-1 transition-colors hover:bg-blue-50" data-id="element-316">
                    + Add
                  </button>
                </div>
                <div className="space-y-4" data-id="element-317">
                  {parties.filter(p => p.role === 'Seller').map(party => <div key={party.id} className="bg-white p-4 rounded-lg border border-slate-200 border-l-2 border-l-blue-500 shadow-sm" data-id="element-318">
                        <div className="grid grid-cols-1 gap-y-5 gap-x-4 sm:grid-cols-6" data-id="element-319">
                          <div className="sm:col-span-2" data-id="element-320">
                            <label className="block text-xs font-medium text-slate-600" data-id="element-321">
                              Full Name
                            </label>
                            <input type="text" value={party.name} readOnly className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 sm:leading-6 bg-white" data-id="element-322" />
                          </div>
                          <div className="sm:col-span-2" data-id="element-323">
                            <label className="block text-xs font-medium text-slate-600" data-id="element-324">
                              Email
                            </label>
                            <input type="email" value={party.email} readOnly className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 sm:leading-6 bg-white" data-id="element-325" />
                          </div>
                          <div className="sm:col-span-2" data-id="element-326">
                            <label className="block text-xs font-medium text-slate-600" data-id="element-327">
                              Phone
                            </label>
                            <input type="text" value={party.phone} readOnly className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 sm:leading-6 bg-white" data-id="element-328" />
                          </div>
                        </div>
                      </div>)}
                </div>
              </div>
            </>}
        </div>
  }, {
    id: 'notes',
    title: 'Notes & Attachments',
    isComplete: true,
    summary: notes ? 'Notes added' : 'No notes',
    content: <div className="space-y-5" data-id="element-329">
          <div data-id="element-330">
            <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-331">
              Order Notes / Memo
            </label>
            <textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} className="block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" placeholder="Any special instructions for the title agency..." data-id="element-332" />
          </div>

          <div data-id="element-333">
            <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-334">
              Documents
            </label>
            <div className="flex justify-center rounded-lg border border-dashed border-slate-300 px-6 py-8 hover:bg-slate-50 transition-colors cursor-pointer bg-white" data-id="element-335">
              <div className="text-center" data-id="element-336">
                <UploadIcon className="mx-auto h-8 w-8 text-slate-300" aria-hidden="true" data-id="element-337" />
                <div className="mt-3 flex text-sm leading-6 text-slate-600 justify-center" data-id="element-338">
                  <span className="relative cursor-pointer rounded-md bg-transparent font-medium text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-500" data-id="element-339">
                    Upload a file
                  </span>
                  <p className="pl-1" data-id="element-340">or drag and drop</p>
                </div>
                <p className="text-xs leading-5 text-slate-500 mt-1" data-id="element-341">
                  PDF, DOCX up to 10MB
                </p>
              </div>
            </div>
          </div>
        </div>
  }];
  return <div className="flex flex-col h-full bg-slate-50 overflow-hidden" data-id="element-342">
      <div className="flex-1 overflow-y-auto p-6 scroll-smooth" data-id="element-343">
        <div className="max-w-3xl mx-auto" data-id="element-344">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 divide-y divide-slate-200" data-id="element-345">
            {sections.map((section, index) => {
            const isExpanded = expandedSection === section.id;
            const stepNumber = index + 1;
            return <div key={section.id} className="flex flex-col" data-id="element-346">
                  {/* Accordion Header */}
                  <div className="cursor-pointer px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" onClick={() => toggleSection(section.id)} data-id="element-347">
                    <div className="flex items-center gap-3" data-id="element-348">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${section.isComplete ? 'bg-emerald-100 text-emerald-700' : isExpanded ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-349">
                        {section.isComplete ? <CheckIcon className="h-4 w-4" data-id="element-350" /> : stepNumber}
                      </div>
                      <h2 className={`text-sm font-semibold transition-colors ${isExpanded ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-351">
                        {section.title}
                      </h2>
                    </div>

                    <div className="flex items-center gap-3" data-id="element-352">
                      {!isExpanded && <span className="text-xs text-slate-500 truncate max-w-[200px] sm:max-w-xs hidden sm:block" data-id="element-353">
                          {section.summary}
                        </span>}
                      <ChevronRightIcon className={`h-5 w-5 text-slate-400 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-354" />
                    </div>
                  </div>

                  {/* Accordion Body */}
                  <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-355">
                    <div className="px-6 pb-6 pt-3" data-id="element-356">{section.content}</div>
                  </div>
                </div>;
          })}
          </div>

          {/* Submit Button Area */}
          <div className="mt-8 flex justify-end" data-id="element-357">
            <button onClick={handleSubmit} disabled={isSubmitting || !selectedAgency} className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-358">
              {isSubmitting ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-359">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-360"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-361"></path>
                  </svg>
                  Submitting...
                </> : 'Submit Title Order'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}